import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { getShiftDefinitions } from "../../apis/shiftDefinition";
import { getOnDutyByRole } from "../../apis/shiftAssignment";
import AddShiftDefinition from "./AddShiftDefinition";
import WhosOnDutyToday from "./WhosOnDutyToday";
import Modal from "../../components/Modal";
import { LanguageContext } from "../../providers/LanguageProvider";
import { formatTimeToAmPm } from "../../utils/DateUtil";

const DutyRoster = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<any[]>([]);
  const [onDutyData, setOnDutyData] = useState<{
    date?: string;
    shiftDefinition?: { id: number; name: string; startTime: string; endTime: string } | null;
    byRole?: {
      role: { id: number; name: string };
      users: { assignmentId: number; shiftDefinitionId: number; clinicUserId: number; userId: number; name: string }[];
    }[];
  } | null>(null);
  const [onDutyLoading, setOnDutyLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [editableShiftId, setEditableShiftId] = useState<number | null>(null);
  const [isShiftModalOpen, setShiftModalOpen] = useState(false);
  const { translate: t } = useContext(LanguageContext);

  const fetchShifts = () => {
    getShiftDefinitions({ per_page: 100 }).then((res: any) => {
      setShifts(res.data?.data || res.data || []);
    });
  };

  const fetchOnDuty = useCallback(() => {
    setOnDutyLoading(true);
    getOnDutyByRole({
      date: selectedDate,
      shift_definition_id: selectedShiftId ?? undefined,
    })
      .then((res: any) => {
        const raw = res.data?.data ?? res.data;
        setOnDutyData(raw || null);
      })
      .catch(() => setOnDutyData(null))
      .finally(() => setOnDutyLoading(false));
  }, [selectedDate, selectedShiftId]);

  useEffect(() => {
    fetchShifts();
  }, []);

  useEffect(() => {
    fetchOnDuty();
  }, [fetchOnDuty]);

  const handleCloseShiftModal = () => {
    setEditableShiftId(null);
    setShiftModalOpen(false);
    fetchShifts();
  };

  // Derive current shift (whose time range includes now) for "Who's On Duty" display
  const currentShift = useMemo(() => {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    for (const s of shifts) {
      const st = s.startTime ?? "";
      const et = s.endTime ?? "";
      if (!st || !et) continue;
      const [sh, sm] = st.split(":").map(Number);
      const [eh, em] = et.split(":").map(Number);
      const startM = sh * 60 + (sm || 0);
      const endM = eh * 60 + (em || 0);
      if (nowMinutes >= startM && nowMinutes <= endM) {
        return { name: s.name, startTime: st, endTime: et };
      }
    }
    return shifts[0]
      ? {
          name: shifts[0].name,
          startTime: shifts[0].startTime ?? "",
          endTime: shifts[0].endTime ?? "",
        }
      : null;
  }, [shifts]);

  const byRole = onDutyData?.byRole ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("duty_roster") || "Duty Roster"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage shift definitions and assign shifts to users.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-4 flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/leave-records")}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Leave Records
          </button>
          <button
            type="button"
            onClick={() => navigate("/shift-assignments")}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Shift Assignments
          </button>
        </div>
      </div>

      {/* Who's On Duty - wired to on-duty API */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <label className="text-sm font-medium text-gray-700">
          Date:
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="ml-2 rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          Shift:
          <select
            value={selectedShiftId ?? ""}
            onChange={(e) => setSelectedShiftId(e.target.value ? Number(e.target.value) : null)}
            className="ml-2 rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">All shifts</option>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({formatTimeToAmPm(s.startTime ?? "")} – {formatTimeToAmPm(s.endTime ?? "")})
              </option>
            ))}
          </select>
        </label>
      </div>
      <WhosOnDutyToday
        currentShift={
          (() => {
            const sd = onDutyData?.shiftDefinition;
            if (sd) {
              return {
                name: sd.name,
                startTime: sd.startTime ?? "",
                endTime: sd.endTime ?? "",
              };
            }
            return selectedShiftId == null
              ? { name: "All Shifts", startTime: "", endTime: "" }
              : currentShift;
          })()
        }
        byRole={byRole}
        dateLabel={format(new Date(selectedDate), "EEEE, MMMM d, yyyy")}
        loading={onDutyLoading}
        onShiftChanged={fetchOnDuty}
      />

      {/* Shift Definitions */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-medium text-gray-900">Shift Definitions</h2>
          <button
            type="button"
            onClick={() => setShiftModalOpen(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Add Shift
          </button>
        </div>
        <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  {t("name")}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Start Time
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  End Time
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t("actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shifts.length > 0 ? (
                shifts.map((s) => (
                  <tr key={s.id}>
                    <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                      {s.name}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {formatTimeToAmPm(s.startTime ?? "") || "-"}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {formatTimeToAmPm(s.endTime ?? "") || "-"}
                    </td>
                    <td className="px-3 py-4">
                      <button
                        onClick={() => {
                          setEditableShiftId(s.id);
                          setShiftModalOpen(true);
                        }}
                        className="text-sm font-semibold text-primary hover:text-hover"
                      >
                        {t("edit")}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 pr-3 pl-4 text-sm text-gray-500 sm:pl-6">
                    No shifts defined. Add a shift to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isShiftModalOpen && (
        <Modal
          title={editableShiftId ? "Edit Shift" : "Add Shift"}
          onClose={handleCloseShiftModal}
          modalContent={
            <AddShiftDefinition
              editableId={editableShiftId}
              closeModal={handleCloseShiftModal}
            />
          }
        />
      )}
    </div>
  );
};

export default DutyRoster;
